/**
 * 工具列表 Hook
 * 从后端API获取工具列表
 * 
 * v4.0 更新：支持按项目获取工具
 * - 如果传入 projectId，调用 GET /projects/{id}/tools 获取项目可用工具
 * - 如果不传 projectId，调用 GET /tools 获取所有系统工具
 * 
 * v4.0.1 更新：支持静默刷新
 * - 添加 refetch 函数，允许调用者手动刷新工具列表
 * - 静默刷新时不显示 loading 状态，避免 UI 闪烁
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { ToolApiService } from '@/modules/agent/services/tool-api'
import type { ToolListItem, BuiltinToolInfo } from '@/modules/agent/types/tool'

/**
 * 获取工具列表
 * @param projectId 项目 ID（可选）
 *   - 有值时：获取项目可用工具（用于 Agent 节点配置）
 *   - 无值时：获取所有系统工具（用于项目设置页面）
 * @returns { tools, isLoading, error, refetch }
 *   - refetch: 静默刷新工具列表，不触发 loading 状态
 */
export function useTools(projectId?: string) {
  const [tools, setTools] = useState<ToolListItem[]>([])
  const [builtinTools, setBuiltinTools] = useState<BuiltinToolInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // 用于跟踪是否是首次加载
  const isFirstLoad = useRef(true)

  // 加载工具列表的核心函数
  const loadTools = useCallback(async (silent: boolean = false) => {
    // 只有非静默模式且是首次加载时才显示 loading
    if (!silent && isFirstLoad.current) {
      setIsLoading(true)
    }
    
    setError(null)
    
    // 独立获取MCP工具（必须成功）
    try {
      const mcpResponse = projectId 
        ? await ToolApiService.getProjectTools(projectId)
        : await ToolApiService.getTools()
      setTools(mcpResponse.data)
      isFirstLoad.current = false
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载MCP工具失败')
      console.error('Failed to load MCP tools:', err)
    }
    
    // 独立获取builtin工具（允许失败）
    try {
      const builtinResponse = await ToolApiService.getBuiltinTools()
      setBuiltinTools(builtinResponse.data.tools)
    } catch (err) {
      console.warn('Failed to load builtin tools (this is expected if API is not deployed):', err)
      // builtin工具获取失败不影响MCP工具的使用
      setBuiltinTools([])
    }
    
    if (!silent && isFirstLoad.current) {
      setIsLoading(false)
    }
    setIsLoading(false)
  }, [projectId])

  // 首次加载和 projectId 变化时自动加载
  useEffect(() => {
    isFirstLoad.current = true
    loadTools(false)
  }, [loadTools])

  // 静默刷新函数（供外部调用）
  const refetch = useCallback(() => {
    loadTools(true)
  }, [loadTools])

  return { tools, builtinTools, isLoading, error, refetch }
}

